/**
 * Firebase Auth account management for the primary project.
 *
 * The app historically verified passwords client-side against the Firestore
 * `users` collection. That is being replaced with real Firebase Auth accounts
 * on the PRIMARY project (arztaleb-18dde) so that Firestore security rules can
 * identify the caller. This module contains the helpers shared by login,
 * registration and admin account creation.
 *
 * Identity model:
 * - Every login-capable user has a Firebase Auth account whose uid maps to a
 *   `users/{userId}` document via `authMap/{uid} -> { userId }`.
 *   Newly created accounts always have uid === userId.
 * - Accounts that log in with a username (offices) use a deterministic
 *   synthetic email: `<username>@login.arztaleb.com`.
 * - `loginAliases/{username} -> { email }` lets username logins resolve to the
 *   real Auth email (needed for Normal users whose username differs from their
 *   email prefix domain).
 */
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
    getAuth,
    createUserWithEmailAndPassword,
    signOut,
    Auth,
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, Firestore } from 'firebase/firestore';
import { primaryFirebaseConfig, db } from '../firebase';

/** Domain used to build synthetic login emails for username-only accounts. */
export const LOGIN_EMAIL_DOMAIN = 'login.arztaleb.com';

export const isEmail = (value: string): boolean => /.+@.+\..+/.test(value.trim());

/** Deterministic synthetic email for a username-only account (offices, etc.). */
export const syntheticEmailForUsername = (username: string): string =>
    `${username.trim().toLowerCase()}@${LOGIN_EMAIL_DOMAIN}`;

const SECONDARY_APP_NAME = 'account-management';

/**
 * A secondary Firebase app instance used to create accounts without touching
 * the main app's auth session (creating a user signs that app in as them).
 */
const getSecondaryApp = (): FirebaseApp => {
    const existing = getApps().find(a => a.name === SECONDARY_APP_NAME);
    if (existing) return existing;
    return initializeApp(primaryFirebaseConfig, SECONDARY_APP_NAME);
};

export const getSecondaryAuth = (): Auth => getAuth(getSecondaryApp());
export const getSecondaryDb = (): Firestore => getFirestore(getSecondaryApp());

/**
 * Resolve the email to try against Firebase Auth for a login input.
 * Order: raw email -> loginAliases lookup -> synthetic username email.
 */
export const resolveLoginEmail = async (usernameOrEmail: string): Promise<string> => {
    const input = usernameOrEmail.trim().toLowerCase();
    if (isEmail(input)) return input;
    try {
        const aliasDoc = await getDoc(doc(db, 'loginAliases', input));
        if (aliasDoc.exists() && aliasDoc.data().email) {
            return aliasDoc.data().email as string;
        }
    } catch (e) {
        // Alias lookup is best-effort; fall through to the synthetic email.
        console.warn('loginAliases lookup failed:', e);
    }
    return syntheticEmailForUsername(input);
};

export interface CreatedAccount {
    uid: string;
    email: string;
}

/**
 * Create a Firebase Auth account on the primary project WITHOUT disturbing the
 * caller's session. Returns the new uid; the secondary session stays signed in
 * until `finishAccountCreation` is called so that self-registration can write
 * its own Firestore documents under the new identity.
 */
export const createAuthAccount = async (email: string, password: string): Promise<CreatedAccount> => {
    const secondaryAuth = getSecondaryAuth();
    const cred = await createUserWithEmailAndPassword(secondaryAuth, email.trim().toLowerCase(), password);
    return { uid: cred.user.uid, email: cred.user.email || email.trim().toLowerCase() };
};

/** Sign the secondary (account-creation) session out. Always call when done. */
export const finishAccountCreation = async (): Promise<void> => {
    try {
        await signOut(getSecondaryAuth());
    } catch (e) {
        console.warn('Failed to sign out secondary auth session:', e);
    }
};

/**
 * Write the authMap entry linking a Firebase Auth uid to a users document.
 * `useSecondary` writes with the freshly-created account's own identity
 * (needed during self-registration when the main app is not signed in).
 */
export const writeAuthMap = async (uid: string, userId: string, useSecondary: boolean): Promise<void> => {
    const database = useSecondary ? getSecondaryDb() : db;
    await setDoc(doc(database, 'authMap', uid), { userId });
};

/** Register a username -> email alias so username logins keep working. */
export const writeLoginAlias = async (username: string, email: string, useSecondary: boolean): Promise<void> => {
    const uname = username.trim().toLowerCase();
    if (!uname) return;
    const database = useSecondary ? getSecondaryDb() : db;
    try {
        await setDoc(doc(database, 'loginAliases', uname), { email: email.trim().toLowerCase() });
    } catch (e) {
        // Alias may already exist (rules make aliases create-only). Not fatal:
        // the account still works via its email.
        console.warn(`Could not write login alias for "${uname}":`, e);
    }
};
